import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { CircularProgress, makeStyles } from '@material-ui/core'
import Icon from '../../atoms/Icon'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import { useHideIfNoDataWhen } from '../HideIfNoData'
import useBlueprintData from './hooks/useBlueprintData'
import useStyleVariant from './hooks/useStyleVariant'
import { LATCH_LATEST } from './constants'

const SIZE_STYLES = {
  sm: { title: '0.875rem', metric: '1.5rem', icon: '32px', padding: '8px' },
  md: { title: '1rem', metric: '2rem', icon: '40px', padding: '10px' },
  lg: { title: '1.25rem', metric: '2.5rem', icon: '50px', padding: '12px' },
  xl: { title: '1.5rem', metric: '3rem', icon: '60px', padding: '16px' }
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.layout.gap.tight,
    padding: ({ sizeKey }) => SIZE_STYLES[sizeKey]?.padding || '10px'
  },
  iconArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `0 ${theme.layout.padding.thick}`,
    fill: 'var(--color-primary-500)'
  },
  urlIcon: {
    objectFit: 'contain'
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.layout.gap.tight,
    flex: 1
  },
  title: {
    fontSize: ({ sizeKey }) => SIZE_STYLES[sizeKey]?.title || '1rem'
  },
  metric: {
    fontSize: ({ sizeKey }) => SIZE_STYLES[sizeKey]?.metric || '2rem'
  },
  clickable: {
    cursor: 'pointer',
    userSelect: 'none'
  },
  footer: {
    fontSize: '0.875rem',
    color: 'var(--text-color-subtle)',
    transition: 'opacity 0.3s ease'
  }
}))

// Matches the DA Metric pattern (Metric.jsx lines 40-57). State is initialised
// from formats[0] on mount and only changes when the user clicks to cycle.
// Formats come from the blueprint definition which is fetched once per session,
// so there is no runtime scenario where the props change under a mounted instance.
function useMultipleFormats (primaryFormat, additionalFormats) {
  const formats = useMemo(() => {
    const all = [primaryFormat, ...(additionalFormats || [])].filter(Boolean)
    return all.length > 0 ? all : [null]
  }, [primaryFormat, additionalFormats])

  const [currentFormat, setCurrentFormat] = useState(formats[0])
  const hasMultiple = formats.length > 1

  const cycle = useCallback(() => {
    if (!hasMultiple) return
    setCurrentFormat(prev => {
      const idx = formats.indexOf(prev)
      return formats[(idx + 1) % formats.length]
    })
  }, [formats, hasMultiple])

  return { currentFormat, hasMultiple, cycle }
}

// V1: `format` is applied uniformly to every {{placeholder}} value. Per-placeholder
// formatting (e.g. {{asOfDate:date}}) can be added later if templates need mixed types.
function resolveFooter (template, format, row, formatter) {
  if (!template || !row) return template || null
  return template.replace(/\{\{([^}]+)\}\}/g, (_, key) => {
    const val = row[key]
    if (val == null) return ''
    return format ? formatter(val, format) : String(val)
  })
}

function BlueprintMetric ({ blueprintCode, config, latch, parameters, scopePredicates, definition }) {
  const {
    purposeCode,
    accessor,
    title,
    primaryFormat,
    additionalFormats,
    icon,
    footer,
    size: sizeKey = 'md',
    align = 'left'
  } = config

  const isConfigured = !!purposeCode && !!accessor

  // All hooks must be called unconditionally (Rules of Hooks).
  // Data-fetching hooks use enabled flags to no-op when config is incomplete.
  const classes = useStyles({ sizeKey })
  const variant = useStyleVariant('Metric', config)
  const { formatter } = useFormattingContext()

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const { data, isLoading } = useBlueprintData(
    blueprintCode,
    isConfigured ? purposeCode : null,
    { parameters: effectiveParameters, pageSize: 1, scopePredicates, definition }
  )
  useHideIfNoDataWhen(() => isLoading || !data?.length, [isLoading, data])

  const row = data?.[0] ?? null
  const rawValue = row?.[accessor] ?? null

  const { currentFormat, hasMultiple, cycle } = useMultipleFormats(primaryFormat, additionalFormats)

  const formattedValue = useMemo(() => {
    if (rawValue == null) return '--'
    return currentFormat ? formatter(rawValue, currentFormat) : String(rawValue)
  }, [rawValue, currentFormat, formatter])

  const footerText = useMemo(() => {
    if (!footer?.template) return null
    return resolveFooter(footer.template, footer.format, row, formatter)
  }, [footer, row, formatter])

  if (!isConfigured) {
    return null
  }

  const alignClass = `text-${align}`
  const iconSize = SIZE_STYLES[sizeKey]?.icon || '40px'

  return (
    <div
      className={clsx(classes.root, variant.className, { [classes.clickable]: hasMultiple })}
      {...(hasMultiple && {
        onClick: cycle,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            cycle()
          }
        }
      })}
    >
      {icon && !variant.props.hideIcon && (
        <div className={classes.iconArea}>
          {icon.type === 'library'
            ? <Icon name={icon.value} customSize={iconSize} />
            : (
              <img
                src={icon.value}
                alt=''
                className={classes.urlIcon}
                style={{ width: iconSize, height: iconSize }}
              />
            )}
        </div>
      )}
      <div className={classes.body}>
        {title && (
          <div className={clsx(classes.title, alignClass)}>{title}</div>
        )}
        {isLoading
          ? <CircularProgress size={parseInt(SIZE_STYLES[sizeKey]?.icon, 10) || 32} />
          : <div className={clsx(classes.metric, alignClass)}>{formattedValue}</div>}
        {footer?.template && (
          <div
            className={clsx(classes.footer, alignClass)}
            style={{ opacity: isLoading ? 0 : 1 }}
          >
            {footerText}
          </div>
        )}
      </div>
    </div>
  )
}

BlueprintMetric.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    purposeCode: PropTypes.string,
    accessor: PropTypes.string,
    title: PropTypes.string,
    primaryFormat: PropTypes.string,
    additionalFormats: PropTypes.arrayOf(PropTypes.string),
    icon: PropTypes.shape({
      type: PropTypes.oneOf(['library', 'url']).isRequired,
      value: PropTypes.string.isRequired
    }),
    footer: PropTypes.shape({
      template: PropTypes.string.isRequired,
      format: PropTypes.string
    }),
    size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
    align: PropTypes.oneOf(['left', 'center', 'right'])
  }).isRequired,
  latch: PropTypes.shape({
    parameterName: PropTypes.string.isRequired,
    dateColumn: PropTypes.string.isRequired,
    dateTable: PropTypes.string.isRequired,
    limit: PropTypes.number
  }),
  parameters: PropTypes.object,
  scopePredicates: PropTypes.arrayOf(PropTypes.object),
  definition: PropTypes.object
}

BlueprintMetric.defaultProps = {
  parameters: {}
}

export default BlueprintMetric
